import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, ChevronRight, ChevronLeft, Check, Eye, EyeOff, Landmark, ShieldCheck, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const steps = [
  { title: "Personal Info", description: "Let's start with the basics", fields: ["firstName", "lastName", "dateOfBirth", "gender", "maritalStatus"] },
  { title: "Location", description: "Your current place of residence", fields: ["country", "state", "address"] },
  { title: "Account Setup", description: "How you'll access your vault", fields: ["email", "phone", "currency"] },
  { title: "Security", description: "Protect your wealth with a PIN", fields: ["password", "confirmPassword", "transactionPin"] },
];

const countries = ["United States", "United Kingdom", "Nigeria", "Canada", "Australia", "Germany", "France"];
const currencies = [
  { value: "$", label: "USD ($)" },
  { value: "€", label: "EUR (€)" },
  { value: "£", label: "GBP (£)" },
  { value: "₦", label: "NGN (₦)" },
];

export const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateOfBirth: "",
    gender: "",
    maritalStatus: "",
    country: "",
    state: "",
    address: "",
    currency: "$",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    transactionPin: "",
    referralCode: "" // Added based on your Nodejs structure
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
  };

  const validateStep = () => {
    const currentFields = steps[currentStep].fields;
    for (const field of currentFields) {
      if (!formData[field]) {
        setError(`Please fill in all fields`);
        return false;
      }
    }
    if (currentStep === 3) {
      if (formData.password !== formData.confirmPassword) {
        setError("Passwords don't match");
        return false;
      }
      if (formData.password.length < 6) {
        setError("Password must be at least 6 characters");
        return false;
      }
      if (formData.transactionPin.length !== 4) {
        setError("Transaction PIN must be 4 digits");
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;

    setLoading(true);
    setError("");

    try {
      const result = await signup({
        first_name: formData.firstName,
        last_name: formData.lastName,
        date_of_birth: formData.dateOfBirth,
        gender: formData.gender,
        marital_status: formData.maritalStatus,
        country: formData.country,
        state: formData.state,
        address: formData.address,
        currency: formData.currency,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        transaction_pin: formData.transactionPin,
        referral_code: formData.referralCode
      });
      
      setSuccess(result);
      toast.success("Account created successfully!");
    } catch (err) {
      setError(err.response?.data?.message || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
 return (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans text-slate-900">
    <div className="w-full max-w-[450px] bg-white rounded-[3.5rem] shadow-2xl p-10 text-center border border-slate-200/60 animate-in zoom-in-95 duration-500">
      
      {/* Icon Section */}
      <div className="relative mx-auto mb-8 w-24 h-24">
        <div className="absolute inset-0 bg-green-500/10 rounded-full animate-ping duration-1000" />
        <div className="relative w-full h-full rounded-full bg-green-50 flex items-center justify-center border-4 border-white shadow-sm">
          <Check className="w-12 h-12 text-green-500" />
        </div>
      </div>

      {/* Title Section */}
      <div className="mb-8">
        <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">
          Account Created!
        </h2>
        <p className="text-slate-400 text-sm mt-3 font-medium px-4 leading-relaxed">
          Your account is ready. Securely store your credentials to access your assets.
        </p>
      </div>

      {/* Credentials Box */}
      <div className="space-y-4 mb-10">
        <div className="group p-5 bg-slate-100 rounded-3xl border border-slate-200 transition-all hover:bg-white hover:shadow-lg hover:border-transparent">
          <span className="block text-[12px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 transition-colors group-hover:text-blue-500">
            Account Number
          </span>
          <span className="text-2xl font-mono font-black text-slate-900 tracking-wider">
            {success.account_number}
          </span>
        </div>

        <div className="group p-5 bg-slate-100 rounded-3xl border border-slate-200 transition-all hover:bg-white hover:shadow-lg hover:border-transparent">
          <span className="block text-[12px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 transition-colors group-hover:text-indigo-500">
            Transaction Code
          </span>
          <span className="text-2xl font-mono font-black text-slate-900 tracking-wider">
            {success.transaction_code}
          </span>
        </div>
      </div>

      {/* Action Section */}
      <div className="space-y-4">
        <Button 
          onClick={() => navigate("/login")} 
          className="w-full h-16 bg-slate-900 text-white rounded-2xl font-bold text-lg shadow-xl shadow-slate-200 hover:bg-slate-800 hover:-translate-y-0.5 transition-all active:scale-95"
          data-testid="go-to-login-btn"
        >
          Proceed to Login
        </Button>
        
        <p className="text-[12px] font-bold text-red-500 uppercase tracking-tight">
          Warning: Do not share your transaction code with anyone.
        </p>
      </div>
    </div>
  </div>
);
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-0 md:p-6 font-sans">
      <div className="w-full max-w-[450px] h-screen md:h-[880px] bg-white md:rounded-[3rem] shadow-2xl overflow-hidden flex flex-col relative border border-slate-200/60">
        
        {/* Background Decoration */}
        <div className="absolute -top-12 -left-12 pointer-events-none">
          <Landmark className="w-64 h-64 text-blue-600 opacity-[0.03] -rotate-12" />
        </div>

        {/* Header & Progress */}
        <header className="px-10 pt-14 pb-6 z-10">
          <div className="flex justify-between items-center mb-6">
            <button 
              type="button"
              onClick={currentStep > 0 ? prevStep : () => navigate("/")} 
              className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition-colors"
              data-testid="prev-step-btn"
            >
              <ChevronLeft size={20} className="text-slate-600" />
            </button>
            <div className="flex flex-col items-center">
              <span className="text-[12px] font-black text-slate-300 uppercase tracking-[0.2em]">Security Level</span>
              <div className="flex gap-1 mt-1">
                 {[0, 1, 2, 3].map(i => (
                   <div key={i} className={cn("h-1 w-6 rounded-full transition-all duration-500", i <= currentStep ? "bg-blue-600" : "bg-slate-100")} />
                 ))}
              </div>
            </div>
            <div className="w-10" />
          </div>

          <h1 className="text-3xl font-black text-slate-900 tracking-tight leading-tight">{steps[currentStep].title}</h1>
          <p className="text-slate-400 text-sm mt-1">{steps[currentStep].description}</p>
            {error && (
            <Alert variant="destructive" className="mt-3 rounded-2xl bg-red-50 border-none text-red-600 animate-in fade-in slide-in-from-top-2">
              <AlertDescription className="text-sm font-bold flex items-center gap-2">
                <ShieldCheck size={14} /> {error}
              </AlertDescription>
            </Alert>
          )}

        </header>

        {/* Scrollable Body */}
        <div className="flex-1 px-10 overflow-y-auto no-scrollbar z-10">
        
          <form onSubmit={currentStep === steps.length - 1 ? handleSubmit : (e) => { e.preventDefault(); nextStep(); }} className="space-y-6 pb-6">
            {currentStep === 0 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-slate-500 ml-1">FIRST NAME</Label>
                    <Input 
                      className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base placeholder:text-slate-400" 
                      value={formData.firstName} 
                      onChange={(e) => handleChange("firstName", e.target.value)} 
                      placeholder="John" 
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-slate-500 ml-1">LAST NAME</Label>
                    <Input 
                      className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base placeholder:text-slate-400" 
                      value={formData.lastName} 
                      onChange={(e) => handleChange("lastName", e.target.value)} 
                      placeholder="Doe" 
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">DATE OF BIRTH</Label>
                  <Input 
                    type="date" 
                    className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" 
                    value={formData.dateOfBirth} 
                    onChange={(e) => handleChange("dateOfBirth", e.target.value)} 
                    data-testid="dob-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-slate-500 ml-1">GENDER</Label>
                    <Select value={formData.gender} onValueChange={(v) => handleChange("gender", v)}>
                      <SelectTrigger className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" data-testid="gender-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] font-bold text-slate-500 ml-1">MARITAL STATUS</Label>
                    <Select value={formData.maritalStatus} onValueChange={(v) => handleChange("maritalStatus", v)}>
                      <SelectTrigger className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" data-testid="marital-status-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Single">Single</SelectItem>
                        <SelectItem value="Married">Married</SelectItem>
                        <SelectItem value="Divorced">Divorced</SelectItem>
                        <SelectItem value="Widowed">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 1 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">COUNTRY</Label>
                  <Select value={formData.country} onValueChange={(v) => handleChange("country", v)}>
                    <SelectTrigger className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" data-testid="country-select">
                      <SelectValue placeholder="Choose Country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">STATE / PROVINCE</Label>
                  <Input 
                    className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base placeholder:text-slate-400" 
                    value={formData.state} 
                    onChange={(e) => handleChange("state", e.target.value)} 
                    placeholder="e.g. New York" 
                    data-testid="state-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">FULL ADDRESS</Label>
                  <Input 
                    className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base placeholder:text-slate-400" 
                    value={formData.address} 
                    onChange={(e) => handleChange("address", e.target.value)} 
                    placeholder="Street, Building, Suite" 
                    data-testid="address-input"
                  />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">EMAIL ADDRESS</Label>
                  <Input 
                    type="email" 
                    className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base placeholder:text-slate-400" 
                    value={formData.email} 
                    onChange={(e) => handleChange("email", e.target.value)} 
                    placeholder="name@company.com" 
                    data-testid="email-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">PHONE NUMBER</Label>
                  <Input 
                    type="tel" 
                    className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base placeholder:text-slate-400" 
                    value={formData.phone} 
                    onChange={(e) => handleChange("phone", e.target.value)} 
                    placeholder="+1234567890" 
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">PREFERRED CURRENCY</Label>
                  <Select value={formData.currency} onValueChange={(v) => handleChange("currency", v)}>
                    <SelectTrigger className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5 pt-2">
                  <Label className="text-[11px] font-bold text-blue-600 ml-1 flex items-center gap-1"><Gift size={10} /> REFERRAL CODE (OPTIONAL)</Label>
                  <Input 
                    className="h-12 rounded-2xl bg-blue-50 border border-blue-100 text-slate-900 text-base placeholder:text-slate-400" 
                    value={formData.referralCode} 
                    onChange={(e) => handleChange("referralCode", e.target.value.toUpperCase())} 
                    placeholder="VAULT-XXXX" 
                  />
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-500">
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">PASSWORD</Label>
                  <div className="relative">
                    <Input 
                      type={showPassword ? "text" : "password"} 
                      className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" 
                      value={formData.password} 
                      onChange={(e) => handleChange("password", e.target.value)} 
                      data-testid="password-input"
                      placeholder="Min 6 characters"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">CONFIRM PASSWORD</Label>
                  <Input 
                    type={showPassword ? "text" : "password"} 
                    className="h-12 rounded-2xl bg-slate-100 border border-slate-200 text-slate-900 text-base" 
                    value={formData.confirmPassword} 
                    onChange={(e) => handleChange("confirmPassword", e.target.value)} 
                    data-testid="confirm-password-input"
                    placeholder="Re-enter password"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[11px] font-bold text-slate-500 ml-1">4-DIGIT TRANSACTION PIN</Label>
                  <div className="relative">
                    <Input 
                      type={showPin ? "text" : "password"} 
                      maxLength={4} 
                      className="h-14 rounded-2xl bg-slate-900 text-white border-none tracking-[1.5em] text-center font-black text-xl focus-visible:ring-2 focus-visible:ring-blue-600/50" 
                      value={formData.transactionPin} 
                      onChange={(e) => handleChange("transactionPin", e.target.value.replace(/\D/g, "").slice(0, 4))} 
                      data-testid="pin-input"
                      placeholder="****"
                    />
                    <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                      {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <p className="text-[12px] text-slate-400 text-center mt-2">Required for all transfers and withdrawals</p>
                </div>
              </div>
            )}

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                className="w-full h-14 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl font-bold shadow-xl shadow-blue-100 hover:scale-[1.02] active:scale-95 transition-all"
                disabled={loading}
                data-testid={currentStep === steps.length - 1 ? "submit-btn" : "next-step-btn"}
              >
                {loading ? <Loader2 className="animate-spin" /> : currentStep === steps.length - 1 ? "Create My Account" : (
                  <span className="flex items-center">
                    Next Step <ChevronRight size={18} className="ml-1" />
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <footer className="px-10 py-10 z-20 bg-white border-t border-slate-50">
          <div className="flex flex-col items-center gap-4">
             <p className="text-sm text-slate-500 font-medium">
               Already a member? <Link to="/login" className="text-blue-600 font-bold ml-1 hover:underline" data-testid="login-link">Log In</Link>
             </p>
             <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 rounded-full">
                <ShieldCheck className="text-green-500" size={12} />
                <span className="text-[12px] font-bold text-slate-500 uppercase tracking-tight">AES-256 Bank-Grade Encryption</span>
             </div>
          </div>
        </footer>
      </div>
    </div>
  );
};